import { Grid } from '@material-ui/core';
import { ModalWrapper } from 'components';
import { useIsAlternativeAuction } from 'hooks';
import moment from 'moment';
import { FormSpy, useForm } from 'react-final-form';
import { normalizeFormattedField } from 'utils';
import { ConfirmDropReserveModal } from '../ConfirmDropReservePriceModal';
import {
  AlternativeOfferField,
  CostField,
  NotesField,
  ReservePriceField,
  StartingBidField,
  StockField
} from './fields';
import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { setFocusField } from '../../../../../services/mySales/salesSlice';
import ConfirmModal from '../../../../../components/Modals/ConfirmModal';
import { modalsToggle } from '../../../../../actions';

type Props = {
  handleSubmit: any;
  modalName: string;
  dropReserveModalOpen: boolean;
  setDropReserveModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
};

const alternativeAuctionFields = [
  <AlternativeOfferField />,
  <ReservePriceField />,
  <StartingBidField />,
  <StockField />,
  <CostField />,
  <NotesField />
];

const aaFields = [<StockField />, <CostField />, <ReservePriceField />, <StartingBidField />, <NotesField />];

export const EditVehicleBidForm = ({
  handleSubmit,
  modalName,
  dropReserveModalOpen,
  setDropReserveModalOpen
}: Props) => {
  const form = useForm();
  const dispatch = useDispatch();
  const { submitting, values, dirty } = form.getState();

  const vehicleTimeEnded = values?.date_end && moment(values.date_end).isBefore(moment());
  const vehicleReservedPriceMet = values?.reserve_price_met;

  const isAlternativeAuction = useIsAlternativeAuction(values as any);

  const fields = isAlternativeAuction ? alternativeAuctionFields : aaFields;

  const shouldRenderConfirmModal = (reserve_price: string, highest_bid: string) => {
    const normalizedReservePrice = Number(normalizeFormattedField(reserve_price ?? ''));
    const normalizedHighestBid = Number(normalizeFormattedField(highest_bid ?? ''));
    return normalizedReservePrice <= normalizedHighestBid;
  };

  const handleSubmitButtonClick = () => {
    if (form.getState().hasValidationErrors) {
      return form.submit();
    }
    if (isAlternativeAuction && dirty) {
      return dispatch(modalsToggle(`confirm-update-${values.id}`));
    }

    const renderConfirmModal = shouldRenderConfirmModal(values.reserve_price, values.highest_bid?.amount);
    if (!renderConfirmModal || vehicleTimeEnded || vehicleReservedPriceMet || isAlternativeAuction) {
      return form.submit(); // submitForm(values);
    }

    setDropReserveModalOpen(true);
  };

  return (
    <form id={modalName} onSubmit={handleSubmit}>
      <ModalWrapper
        // @ts-ignore
        title="Edit"
        submitting={submitting}
        handleSubmit={handleSubmitButtonClick}
        modalId={modalName}
        cancelAdditionalAction={form.reset}
        className="edit-vehicle-bid-modal"
        actionsClassName="edit-vehicle-bid-modal-actions"
      >
        <RenderFields fields={fields} />
      </ModalWrapper>
      <ConfirmDropReserveModal
        isOpen={dropReserveModalOpen}
        closeModal={() => setDropReserveModalOpen(false)}
        buttonsDisabled={submitting}
        handleSubmit={handleSubmit}
      />
      <FormSpy
        subscription={{ values: true }}
        onChange={({ values }) => {
          if (isAlternativeAuction) return;
          const normalizedReservePrice = Number(values.reserve_price ?? '');
          const normalizedHighestBid = Number(values.highest_bid?.amount ?? '');
          const normalizedStartingBid = Number(values.starting_bid ?? '');

          if (normalizedStartingBid >= normalizedReservePrice) {
            // this case is handled by error instead
            form.mutators.setFieldData('reserve_price', {
              warning: undefined
            });
          } else {
            const warning =
              normalizedReservePrice <= normalizedHighestBid ? 'Reserve dropped the car will be sold.' : undefined;

            form.mutators.setFieldData('reserve_price', {
              warning
            });
          }
        }}
      />
      <ConfirmModal
        title="You are about to update vehicle"
        handleSubmit={form.submit}
        submitText={'Update'}
        modalId={`confirm-update-${values.id}`}
      />
    </form>
  );
};

const RenderFields = ({ fields }: { fields: JSX.Element[] }) => {
  const dispatch = useDispatch();

  useEffect(() => {
    return () => {
      dispatch(setFocusField(null));
    };
  }, []);

  return <Grid container>{fields}</Grid>;
};
